
import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, TriangleAlert } from 'lucide-react';

import type { MediaLink, TtsToolResponse } from '../../types/api';
import { Button, Card, PlayButton, Scrubber, type AudioPreview } from '../../components/ui';
import styles from './TtsPage.module.css';
import { downloadFile } from '../../lib/download';
import { formatDuration, plural } from './format';

interface Props {
  run: TtsToolResponse;
  segmentTexts: string[] | undefined;
  voiceLabel: string;
  audio: AudioPreview;
}

export function TtsResult({ run, segmentTexts, voiceLabel, audio }: Props) {
  const segments = run.segments;
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [positionMs, setPositionMs] = useState(0);
  const elementRef = useRef<HTMLAudioElement | null>(null);

  const starts = useMemo(() => {
    let acc = 0;
    return segments.map((segment) => {
      const start = acc;
      acc += segment.duration_ms;
      return start;
    });
  }, [segments]);

  const total =
    run.total_duration_ms || segments.reduce((sum, segment) => sum + segment.duration_ms, 0);

  useEffect(() => {
    setIndex(0);
    setPlaying(false);
    setPositionMs(0);
  }, [run.run_id]);

  useEffect(() => {
    if (audio.playingKey) setPlaying(false);
  }, [audio.playingKey]);

  useEffect(() => {
    const element = elementRef.current;
    const segment = segments[index];
    if (!element || !segment) return;
    if (element.src !== segment.audio.url) {
      element.src = segment.audio.url;
      setPositionMs(0);
    }
    if (playing) {
      void element.play().catch(() => setPlaying(false));
    } else {
      element.pause();
    }
  }, [index, playing, segments]);

  const startPlayback = (nextIndex: number) => {
    audio.stop();
    setIndex(nextIndex);
    setPlaying(true);
  };

  const seekWholeRun = (ms: number) => {
    let offset = Math.max(0, Math.min(total, ms));
    let target = segments.length - 1;
    for (let position = 0; position < segments.length; position += 1) {
      const span = segments[position]?.duration_ms ?? 0;
      if (offset < span || position === segments.length - 1) {
        target = position;
        break;
      }
      offset -= span;
    }
    const source = segments[target]?.audio.url;
    if (!source) return;
    audio.stop();
    const element = elementRef.current;
    if (element) {
      if (element.src !== source) element.src = source;
      element.currentTime = offset / 1000;
    }
    setIndex(target);
    setPositionMs(offset);
  };

  const elapsedMs = (starts[index] ?? 0) + positionMs;
  const degradedCount = segments.filter((segment) => segment.degraded).length;

  const download = (audio: MediaLink, name: string) => {
    void downloadFile(audio.url, name);
  };

  const downloadAll = () => {
    segments.forEach((segment, position) => {
      window.setTimeout(
        () => download(segment.audio, `${run.title || 'tts'}-${position + 1}.mp3`),
        position * 350,
      );
    });
  };

  return (
    <Card>
      <div className={styles.resultHead}>
        <PlayButton
          size="lg"
          playing={playing}
          onClick={() => (playing ? setPlaying(false) : startPlayback(index))}
          title={playing ? 'Пауза' : 'Слушать прогон целиком'}
          ariaLabel={playing ? 'Пауза' : 'Слушать прогон'}
        />

        <div className={styles.resultMain}>
          <div className={styles.resultTitleRow}>
            <span className={styles.resultTitle}>Готово</span>
            <span className={styles.resultMeta}>
              {formatDuration(total)} · {voiceLabel}
            </span>
          </div>
          <Scrubber
            positionMs={elapsedMs}
            durationMs={total}
            onSeek={seekWholeRun}
            ariaLabel="Перемотка озвучки"
          />
        </div>

        <Button
          size="sm"
          onClick={() => {
            const first = segments[0];
            if (segments.length === 1 && first) {
              download(first.audio, `${run.title || 'tts'}.mp3`);
            } else {
              downloadAll();
            }
          }}
          title={
            segments.length === 1
              ? 'Скачать mp3'
              : 'Скачается по файлу на каждую часть'
          }
        >
          <Download size={13} strokeWidth={1.5} />
          {segments.length === 1 ? 'Скачать mp3' : `Скачать ${segments.length} файла(ов)`}
        </Button>
      </div>

      {degradedCount > 0 && (
        <div className={styles.degradedNote}>
          <TriangleAlert size={14} strokeWidth={1.5} className={styles.noteIcon} />
          <span>
            У {degradedCount} {plural(degradedCount, 'части', 'частей', 'частей')} тайм-коды
            приблизительные. Звук в порядке, но пословные субтитры по ним не собрать.
          </span>
        </div>
      )}

      {segments.map((segment, position) => {
        const start = starts[position] ?? 0;
        const text = segmentTexts?.[position];
        const isCurrent = position === index && playing;
        return (
          <div key={segment.index} className={styles.segmentRow}>
            <PlayButton
              size="sm"
              playing={isCurrent}
              onClick={() => (isCurrent ? setPlaying(false) : startPlayback(position))}
              ariaLabel={isCurrent ? 'Пауза' : `Слушать часть ${position + 1}`}
            />
            <span className={styles.segmentTime}>
              {formatDuration(start)} – {formatDuration(start + segment.duration_ms)}
            </span>
            <span className={text ? styles.segmentText : styles.segmentTextUnknown}>
              {text ?? 'Текст виден только у свежей озвучки'}
            </span>
            {segment.degraded && (
              <span className={styles.segmentFlag} title={segment.degradations.join(', ')}>
                ≈ тайминги
              </span>
            )}
            <Button
              size="xs"
              iconOnly
              onClick={() =>
                download(segment.audio, `${run.title || 'tts'}-${position + 1}.mp3`)
              }
              aria-label={`Скачать часть ${position + 1}`}
              title="Скачать часть"
            >
              <Download size={11} strokeWidth={1.6} />
            </Button>
          </div>
        );
      })}

      <audio
        ref={elementRef}
        onTimeUpdate={(event) => setPositionMs(event.currentTarget.currentTime * 1000)}
        onEnded={() => {
          if (index + 1 < segments.length) {
            setIndex(index + 1);
            setPositionMs(0);
          } else {
            setPlaying(false);
            setIndex(0);
            setPositionMs(0);
          }
        }}
        hidden
      />
    </Card>
  );
}
